<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NxBadge,
  NxBanner,
  NxButton,
  NxErrorSummary,
  NxFileInput,
  NxKpiCard,
  NxLabel,
  NxModal,
  NxTable,
  NxTableColumn,
  NxTableStringColumn,
  useNxToasts,
} from '@blpde/nx-ui';
import {
  errorSummaryBindings,
  originKey,
  reportWriteFailure,
  useErrorSummary,
  type OriginKey,
} from '../../http';
import type { JsonObject } from '../../config/mergePatch';
import { DICTIONARY_DOCUMENT_MAX_BYTES, DICTIONARY_FILE_MAX_BYTES, decodeDictionaryFile } from '../dictionaryFile';
import type { DictionaryImportPreview, DictionaryImportRow } from '../dictionaryImport';
import { dictionaryWriteFailure, type DictionaryWriteResult } from './level';
import { DICTIONARY_TEXT } from './labels';

/**
 * The import of a file into one tab (I-3024): the browser reads the file with
 * the kit's file input, the tab's `preview` says per row what would happen
 * (new, replaced, unchanged, refused with the reason), and „Übernehmen“ sends
 * ONE merge patch with `origin: "import"` — or nothing, when the layer would
 * pass 1 MiB. The server-side import of the earlier design is gone.
 */
const props = defineProps<{
  title: string;
  /** The file format in one line (`falsch;richtig;sprache`). */
  hint: string;
  /** File types the picker offers. */
  accept: string;
  /** What the file's text would do to the written layer. */
  preview: (text: string) => DictionaryImportPreview;
  /** Writes the patch. */
  apply: (patch: JsonObject) => Promise<DictionaryWriteResult>;
}>();

const open = defineModel<boolean>('open', { required: true });
const { t } = useI18n();
const T = `${DICTIONARY_TEXT}.import`;

interface IssueRow extends Record<string, unknown> {
  id: string;
  line: string;
  key: string;
  reason: string;
}

const files = ref<File[]>([]);
const result = ref<DictionaryImportPreview | null>(null);
const readFailed = ref<string | null>(null);
const applying = ref(false);
let generation = 0;

const { showToast } = useNxToasts();
const summary = useErrorSummary();
const { entries: summaryEntries, onReload, onCopyFailed } = errorSummaryBindings(summary, { showToast, t });
const APPLY: OriginKey = originKey(useId(), 'action', 'apply');
const locked = computed(() => applying.value || summary.locked.value);

watch(files, async (next) => {
  const file = next[0];
  const current = ++generation;
  result.value = null;
  readFailed.value = null;
  summary.clear();
  if (!file) return;
  if (file.size > DICTIONARY_FILE_MAX_BYTES) {
    readFailed.value = t(`${T}.tooLargeFile`, { name: file.name });
    return;
  }
  try {
    const text = decodeDictionaryFile(new Uint8Array(await file.arrayBuffer()));
    if (current !== generation) return;
    result.value = props.preview(text);
  } catch {
    if (current === generation) readFailed.value = t(`${T}.failed`, { name: file.name });
  }
});

watch(open, (isOpen) => {
  if (isOpen) return;
  generation += 1;
  files.value = [];
  result.value = null;
  readFailed.value = null;
  summary.clear();
});

const kpis = computed(() => {
  const counts = result.value?.counts;
  return [
    { key: 'new', tone: 'success' as const, value: counts?.new ?? 0 },
    { key: 'replaced', tone: 'info' as const, value: counts?.replaced ?? 0 },
    { key: 'unchanged', tone: 'neutral' as const, value: counts?.unchanged ?? 0 },
    { key: 'refused', tone: 'danger' as const, value: counts?.refused ?? 0 },
  ];
});

function reasonText(row: DictionaryImportRow): string {
  return row.reason ? t(`${T}.reasons.${row.reason}`, { other: row.other ?? '' }) : '';
}

const issues = computed<IssueRow[]>(() =>
  (result.value?.rows ?? [])
    .filter((row) => row.status === 'refused')
    .map((row, index) => ({
      id: `r${index}`,
      line: row.line ? String(row.line) : '',
      key: row.key,
      reason: reasonText(row),
    })),
);

const percent = computed(() =>
  result.value ? Math.round((result.value.bytes / DICTIONARY_DOCUMENT_MAX_BYTES) * 100) : 0,
);
const canApply = computed(() => !!result.value?.patch && !locked.value);

async function applyImport(): Promise<void> {
  const patch = result.value?.patch;
  if (!patch || locked.value) return;
  applying.value = true;
  const attempt = summary.begin(APPLY);
  try {
    const answer = await props.apply(patch);
    if (answer.ok) {
      summary.resolve(APPLY, attempt);
      open.value = false;
      return;
    }
    reportWriteFailure(dictionaryWriteFailure(answer), {
      origin: APPLY,
      attempt,
      t,
      kind: 'update',
      context: summary,
    });
  } finally {
    applying.value = false;
  }
}

function noop(): Promise<void> {
  return Promise.resolve();
}
</script>

<template>
  <NxModal v-model:open="open" :title="title" :subtitle="hint" size="xwide">
    <template #banner>
      <NxErrorSummary
        data-testid="dictionary-import-error"
        :entries="summaryEntries"
        @reload="onReload"
        @copy-failed="onCopyFailed"
      />
    </template>
    <template #body>
      <div class="dict-import">
        <NxLabel :text="t(`${T}.file`)">
          <div data-testid="dictionary-import-file">
            <NxFileInput v-model="files" :accept="accept" variant="dropzone" layout="list" />
          </div>
        </NxLabel>
        <NxBanner v-if="readFailed" variant="danger" :title="readFailed" data-testid="dictionary-import-read-failed" />
        <NxBanner
          v-if="result"
          :variant="result.tooLarge ? 'danger' : 'info'"
          :title="result.tooLarge ? t(`${T}.tooLargeTitle`) : t(`${T}.previewTitle`)"
          data-testid="dictionary-import-preview"
        >
          {{ result.tooLarge ? t(`${T}.tooLargeBody`, { percent }) : t(`${T}.previewBody`) }}
        </NxBanner>
        <div v-if="result" class="dict-figures">
          <NxKpiCard
            v-for="kpi in kpis"
            :key="kpi.key"
            :top-text="t(`${T}.kpi.${kpi.key}`)"
            :tone="kpi.tone"
            :data-testid="`dictionary-kpi-${kpi.key}`"
          >
            {{ kpi.value }}
          </NxKpiCard>
        </div>
        <div v-if="issues.length" class="dict-rows">
          <!-- @vue-generic {IssueRow} -->
          <NxTable
            :data="issues"
            :row-id-key="(row: IssueRow) => row.id"
            :pagination-limit="50"
            hide-create-button
            :on-table-state-change="noop"
          >
            <NxTableStringColumn column-id="line" :display-name="t(`${T}.columns.line`)" :filterable="false" />
            <NxTableColumn column-id="key" :display-name="t(`${T}.columns.entry`)" :filterable="false" :to-csv-string="(row: IssueRow) => row.key">
              <template #cell="{ rowData }">
                <NxBadge variant="danger">{{ rowData.key }}</NxBadge>
              </template>
            </NxTableColumn>
            <NxTableStringColumn column-id="reason" :display-name="t(`${T}.columns.reason`)" :filterable="false" />
          </NxTable>
        </div>
      </div>
    </template>
    <template #footer>
      <NxButton variant="outline" @click="open = false">{{ t(`${T}.cancel`) }}</NxButton>
      <NxButton data-testid="dictionary-apply" :disabled="!canApply" :loading="applying" @click="applyImport">
        {{ t(`${T}.apply`) }}
      </NxButton>
    </template>
  </NxModal>
</template>

<style scoped>
.dict-import {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
}
.dict-figures {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--nxt-space-xs);
}
.dict-rows {
  height: calc(var(--nxt-size-unit) * 11.25);
  display: flex;
  flex-direction: column;
}
</style>
